import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export type QueryHistoryItem = {
  /**
   * The appId
   */
  appId: string
  /**
   * The query string
   */
  query: string
  /**
   * When the query was first added to the history
   * (not used for anything right now, but worth keeping)
   */
  createdAt: number
  /**
   * When the query was last used
   */
  lastUsed: number
  /**
   * Whether the query is a favorite
   */
  isFavorite: boolean
  /**
   * The active view the query was executed in
   */
  activeView?: 'collections' | 'editor'
}

interface QueryHistoryStore {
  /**
   * The queries in the history
   */
  queries: QueryHistoryItem[]
  /**
   * Add a query to the history
   */
  addQuery: (query: QueryHistoryItem) => void
  /**
   * Update the last used time for a query
   */
  updateLastUsedForQuery: (
    query: string,
    activeView: 'collections' | 'editor' | undefined,
  ) => void
  /**
   * Remove a query from the history
   */
  removeQuery: (query: QueryHistoryItem) => void
  /**
   * Toggle isFavorite for a query
   */
  toggleFavorite: (query: QueryHistoryItem) => void
}

export const useQueryHistoryStore = create<QueryHistoryStore>()(
  persist(
    (set, get) => ({
      queries: [],

      addQuery: (query: QueryHistoryItem) => {
        if (!query?.appId || !query?.query) return

        // First, check if the query already exists
        const existingQuery = get().queries.find((q) => q.query === query.query)

        // If the query exists, update the last used time
        if (existingQuery) {
          get().updateLastUsedForQuery(query.query, query?.activeView)
          return
        }

        // If the query doesn't exist, add it to the history
        set((old) => ({
          queries: [...old.queries, query],
        }))
      },

      updateLastUsedForQuery: (
        query: string,
        activeView?: 'collections' | 'editor',
      ) => {
        set((old) => ({
          queries: old.queries.map((q) =>
            q.query === query
              ? {
                  ...q,
                  lastUsed: new Date().getTime(),
                  // Only add activeView if it is defined
                  ...(!!activeView ? { activeView } : {}),
                }
              : q,
          ),
        }))
      },

      removeQuery: (query: QueryHistoryItem) =>
        set((old) => ({
          queries: old.queries.filter((q) => q !== query),
        })),

      toggleFavorite: (query: QueryHistoryItem) =>
        set((old) => ({
          queries: old.queries.map((q) =>
            q === query ? { ...q, isFavorite: !q.isFavorite } : q,
          ),
        })),
    }),
    {
      name: 'query-history',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
)

// Helpers that split out the queries and favorites, and sorts them
export const useQueryHistory = (appId: string) =>
  useQueryHistoryStore((state) => {
    return state.queries
      .filter((q) => q.appId === appId)
      .sort((a, b) => b.lastUsed - a.lastUsed)
  })

export const useQueryFavorites = (appId: string) =>
  useQueryHistoryStore((state) =>
    state.queries
      .filter((q) => q.isFavorite && q.appId === appId)
      .sort((a, b) => b.lastUsed - a.lastUsed),
  )
